import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { Paper, Typography, Box, CircularProgress, Alert } from '@mui/material';
import { analyticsApi } from '../../services/api';

interface MonthlyExpenseData {
  month: string;
  expenses: number;
  year: number;
}

const YearOverYearChart: React.FC = () => {
  const [data, setData] = useState<MonthlyExpenseData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await analyticsApi.getMonthlyCashFlow(12);
        
        // Transform the cash flow data to show only expenses for last 12 months
        const monthlyExpenses = response.data.map((item: any, index: number) => ({
          month: item.month.substring(0, 3), // Show abbreviated month name
          expenses: item.expenses,
          year: item.year,
          fullMonth: item.month
        }));
        
        setData(monthlyExpenses);
        setError(null);
      } catch (err) {
        setError('Failed to load monthly expenses data');
        console.error('Error fetching monthly expenses data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            p: 2
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            {data.fullMonth}
          </Typography>
          <Typography variant="body2" sx={{ color: '#f44336' }}>
            Expenses: {formatCurrency(payload[0].value)}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 350, sm: 400 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 350, sm: 400 } }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 350, sm: 400 } }}>
      <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
        Monthly Expenses - Last 12 Months
      </Typography>
      <Box sx={{ height: { xs: 250, sm: 300 }, mt: 2, px: 1 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 20, right: 15, left: 10, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="month" 
              stroke="#666"
              fontSize={11}
              angle={-45}
              textAnchor="end"
              height={80}
              interval={0}
            />
            <YAxis 
              tickFormatter={formatCurrency}
              stroke="#666"
              fontSize={12}
              width={60}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar 
              dataKey="expenses" 
              fill="#f44336" 
              name="Monthly Expenses"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

export default YearOverYearChart;